import { Component } from 'react'
import { IClientUID } from '../../../api/clients'
import { getClientFragmentedSystemInfo, IFragmentedSystemInfo } from '../../../api/0x0002/fragmented-systeminfo'
import { toast } from 'react-toastify'
import Loader from '../../../components/loader'
import { mvToTemp } from '../../../utils/ntc'
import { mvToBar } from '../../../utils/piezo'
import { mvToPercent } from '../../../utils/number'
import { readableTime } from '../../../utils/time'
import { getLocale, getString } from '../../../locale'
import { format } from 'date-fns'
import { readableMemory } from '../../../utils/memory'
import classNames from 'classnames'

// Safe wrapper functions for time formatting
const safeFormatDate = (timestamp: number): string => {
    try {
        const date = new Date(timestamp)
        if (isNaN(date.getTime())) {
            return 'Invalid Time'
        }
        return format(date, 'dd/MM/yyyy HH:mm:ss')
    } catch (error) {
        return 'Invalid Time'
    }
}

const safeReadableTime = (seconds: number): string => {
    try {
        if (isNaN(seconds) || seconds < 0) {
            return 'Invalid Time'
        }
        return readableTime(seconds)
    } catch (error) {
        return 'Invalid Time'
    }
}

export enum PumpStatus {
    'Off' = 0,
    'On' = 1,
    'Timed Out' = 3,
    'Insufficient Pressure' = 4,
}

export enum FilterFillerState {
    IDLE = 0,
    RUNNING = 1,
    STOPPED = 2,
}

export const PumpStateFunc: Record<string | number, (title: string) => any> = {
    '0': title => <i className="flex items-center justify-center w-5 h-5 rounded-full bg-red-500">{title}</i>,
    '1': title => <i className="flex items-center justify-center w-5 h-5 rounded-full bg-green-500">{title}</i>,
    '3': title => <i className="flex items-center justify-center w-5 h-5 rounded-full bg-yellow-500">{title}</i>,
    '4': title => <i className="flex items-center justify-center w-5 h-5 rounded-full bg-yellow-500">{title}</i>,
}

export const RegularState: Record<string | number, any> = {
    '0': <i className="flex items-center justify-center w-5 h-5 rounded-full bg-red-500" />,
    '1': <i className="flex items-center justify-center w-5 h-5 rounded-full bg-green-500" />,
}

export const RegularStateFunc: Record<string | number, (title: string) => any> = {
    false: title => <i className="flex items-center justify-center w-5 h-5 rounded-full bg-red-500 text-black dark:text-white">{title}</i>,
    true: title => <i className="flex items-center justify-center w-5 h-5 rounded-full bg-green-500 text-black dark:text-white">{title}</i>,
}

export const FilterFillerStateFunc: Record<string | number, (title: string) => any> = {
    '0': title => <i className="flex items-center justify-center px-3 w-auto h-5 rounded-full bg-red-500">{title}</i>,
    '1': title => <i className="flex items-center justify-center px-3 w-auto h-5 rounded-full bg-green-500">{title}</i>,
    '2': title => <i className="flex items-center justify-center px-3 w-auto h-5 rounded-full bg-yellow-500">{title}</i>,
}

export type IntervalOption = { label: () => string; ms: number }
export const INTERVAL_OPTIONS: Array<IntervalOption> = [
    {
        label: () => getString('time.half_second'),
        ms: 500,
    },
    {
        label: () => `2 ${getString('time.seconds')}`,
        ms: 2000,
    },
]

type Props = {
    clientId: IClientUID
    intervalOption?: number
}

type State = {
    common?: IFragmentedSystemInfo[number]['common']
    sodaUnit?: IFragmentedSystemInfo[number]['sodaUnit']
    aluBlockCoolingUnit?: IFragmentedSystemInfo[number]['aluBlockCoolingUnit']
    iceBlockCoolingUnit?: IFragmentedSystemInfo[number]['iceBlockCoolingUnit']
    ambientUnit?: IFragmentedSystemInfo[number]['ambientUnit']
    circulationPumpUnit?: IFragmentedSystemInfo[number]['circulationPumpUnit']
    heatingUnit?: IFragmentedSystemInfo[number]['heatingUnit']
    relaySolenoids?: IFragmentedSystemInfo[number]['relaySolenoids']
    filterFiller?: IFragmentedSystemInfo[number]['filterFiller']
    shabbatInfo?: IFragmentedSystemInfo[number]['shabbatInfo']

    intervalOption: IntervalOption
}

class FragmentedSystemInfoTab extends Component<Props, State> {
    mounted: boolean = false

    state: State = {
        intervalOption: INTERVAL_OPTIONS[this.props.intervalOption === undefined ? 1 : this.props.intervalOption],
    }

    setState: Component<Props, State>['setState'] = (state, callback) => {
        if (!this.mounted) return
        return super.setState(state as State, callback)
    }

    findFragment = (
        systemInfo: IFragmentedSystemInfo,
        prop: keyof IFragmentedSystemInfo[number],
    ): IFragmentedSystemInfo[number][keyof IFragmentedSystemInfo[number]] | undefined => {
        if (systemInfo === undefined) return undefined
        for (const fragmentVersion in systemInfo) {
            const fragment = systemInfo[fragmentVersion]
            if (fragment === undefined) continue
            if (prop === 'common') {
                const maybeCommon = fragment[prop] as { epoch?: number; uptime?: number; freeHeap?: number; ambient?: number } | undefined
                if (
                    maybeCommon !== undefined &&
                    maybeCommon.epoch !== undefined &&
                    maybeCommon.uptime !== undefined &&
                    maybeCommon.freeHeap !== undefined &&
                    maybeCommon.ambient !== undefined
                ) {
                    return fragment[prop]
                }
                continue
            }
            if (fragment[prop] !== undefined) {
                return fragment[prop]
            }
        }
        return undefined
    }

    componentDidMount = async () => {
        this.mounted = true
        this.loadData()
    }

    componentWillUnmount = () => {
        this.mounted = false
    }

    async loadData() {
        if (!this.mounted) return
        try {
            const systemInfo = (await getClientFragmentedSystemInfo(this.props.clientId)).data
            const newState = {
                common: this.findFragment(systemInfo, 'common') as IFragmentedSystemInfo[number]['common'],
                sodaUnit: this.findFragment(systemInfo, 'sodaUnit') as IFragmentedSystemInfo[number]['sodaUnit'],
                aluBlockCoolingUnit: this.findFragment(systemInfo, 'aluBlockCoolingUnit') as IFragmentedSystemInfo[number]['aluBlockCoolingUnit'],
                iceBlockCoolingUnit: this.findFragment(systemInfo, 'iceBlockCoolingUnit') as IFragmentedSystemInfo[number]['iceBlockCoolingUnit'],
                ambientUnit: this.findFragment(systemInfo, 'ambientUnit') as IFragmentedSystemInfo[number]['ambientUnit'],
                circulationPumpUnit: this.findFragment(systemInfo, 'circulationPumpUnit') as IFragmentedSystemInfo[number]['circulationPumpUnit'],
                heatingUnit: this.findFragment(systemInfo, 'heatingUnit') as IFragmentedSystemInfo[number]['heatingUnit'],
                relaySolenoids: this.findFragment(systemInfo, 'relaySolenoids') as IFragmentedSystemInfo[number]['relaySolenoids'],
                filterFiller: this.findFragment(systemInfo, 'filterFiller') as IFragmentedSystemInfo[number]['filterFiller'],
                shabbatInfo: this.findFragment(systemInfo, 'shabbatInfo') as IFragmentedSystemInfo[number]['shabbatInfo'],
            }
            console.log('newState', newState)
            this.setState(newState)
        } catch (err) {
            toast.error((err as Error).message)
            console.error(err)
        }
        setTimeout(this.loadData.bind(this), this.state.intervalOption.ms)
    }

    render() {
        return (
            <>
                {this.renderTable()}
                {this.renderIntervalSelect()}
            </>
        )
    }

    renderIntervalSelect() {
        const { intervalOption } = this.state
        return (
            <div className="grid grid-cols-3 gap-8">
                <div className="flex items-center dark:text-white">{getString('system_info.update_interval')}:</div>
                {INTERVAL_OPTIONS.map((option, i) => (
                    <div
                        key={i}
                        onClick={() => this.setState({ intervalOption: option })}
                        className={classNames(
                            'flex items-center p-4 bg-white dark:bg-gray-700 dark:text-white shadow-md rounded-xl',
                            intervalOption.ms === option.ms ? 'bg-gray-200 dark:bg-opacity-40 cursor-not-allowed' : 'hover-grow',
                        )}
                    >
                        {option.label()}
                    </div>
                ))}
            </div>
        )
    }

    renderTable() {
        const {
            common,
            sodaUnit,
            iceBlockCoolingUnit,
            aluBlockCoolingUnit,
            ambientUnit,
            circulationPumpUnit,
            heatingUnit,
            relaySolenoids,
            filterFiller,
            shabbatInfo,
        } = this.state
        const hasIceBankTelemetry =
            iceBlockCoolingUnit !== undefined &&
            iceBlockCoolingUnit.compressorState !== undefined &&
            iceBlockCoolingUnit.ntc1 !== undefined &&
            iceBlockCoolingUnit.compressorActiveTime !== undefined &&
            iceBlockCoolingUnit.compressorInactiveTime !== undefined &&
            iceBlockCoolingUnit.iceSenseAvg !== undefined &&
            iceBlockCoolingUnit.iceSenseRaw !== undefined
        const iceBankState = (iceBlockCoolingUnit as { icebankState?: number } | undefined)?.icebankState
        const iceBankStateLabel =
            iceBankState === 0
                ? getString('system_info.icebank_state_idle')
                : iceBankState === 1
                  ? getString('system_info.icebank_state_making_ice')
                  : iceBankState === 2
                    ? getString('system_info.icebank_state_fault')
                    : iceBankState === 3
                      ? getString('system_info.icebank_state_retry_cooldown')
                      : iceBankState !== undefined
                        ? `${getString('system_info.icebank_state_unknown')} (${iceBankState})`
                        : undefined
        return (
            <div className="mb-8 bg-white dark:bg-gray-800 shadow-md rounded-xl flex flex-wrap justify-center items-stretch w-full p-4">
                {common &&
                    this.renderRow(
                        getString('system_info.epoch'),
                        <>
                            <div className="mt-1" style={{ direction: 'ltr' }}>
                                {safeFormatDate(Number(common.epoch) * 1000)}
                            </div>
                            <div>
                                <div className="py-2 pb-0 text-gray-600 dark:text-gray-400 font-semibold">{getString('system_info.uptime')}</div>
                                {safeReadableTime(Number(common.uptime))}
                            </div>
                            <div>
                                <div className="py-2 pb-0 text-gray-600 dark:text-gray-400 font-semibold">{getString('system_info.free_heap')}</div>
                                {readableMemory(Number(common.freeHeap))}
                            </div>
                            <div>
                                <div className="py-2 pb-0 text-gray-600 dark:text-gray-400 font-semibold">{getString('common.ambient_temp')}</div>
                                <div className="mt-1" style={{ direction: 'ltr' }}>
                                    {common.ambient < 3050 ? (
                                        <>
                                            {mvToTemp(common.ambient)[0]} {getString('common.celsius')}
                                        </>
                                    ) : (
                                        <div className="text-sm text-red-500">{getString('common.disconnected')}</div>
                                    )}
                                </div>
                                <div className="mt-3 text-xs">
                                    {common.ambient / 1000}
                                    {getString('common.v')}
                                </div>
                            </div>
                            {common.rtcBatteryInvalid !== undefined && (
                                <div>
                                    <div className="py-2 pb-0 text-gray-600 dark:text-gray-400 font-semibold">{getString('system_info.rtc_battery')}</div>
                                    <div>
                                        {common.rtcBatteryInvalid === 1
                                            ? getString('system_info.rtc_battery_invalid')
                                            : getString('system_info.rtc_battery_ok')}
                                    </div>
                                </div>
                            )}
                        </>,
                        undefined,
                        undefined,
                        'base',
                    )}

                {iceBlockCoolingUnit &&
                    this.renderRow(
                        getString('system_info.cooling_unit'),
                        <div className="mt-3">
                            {hasIceBankTelemetry && (
                                <>
                                    <div className="flex flex-col items-center">{RegularState[Number(iceBlockCoolingUnit.compressorState)]}</div>
                                    <div className="mt-3 text-base text-gray-600 dark:text-gray-400 font-semibold">{getString('system_info.ice_sense')}</div>
                                    <div style={{ direction: 'ltr' }}>
                                        {mvToPercent(iceBlockCoolingUnit.iceSenseAvg!)}%{' '}
                                        <span className="text-sm">({mvToPercent(iceBlockCoolingUnit.iceSenseRaw!)}%)</span>
                                    </div>
                                    <div className="mt-3 text-xs" style={{ direction: 'ltr' }}>
                                        {iceBlockCoolingUnit.iceSenseAvg! / 1000}
                                        {getString('common.v')} ({iceBlockCoolingUnit.iceSenseRaw! / 1000}
                                        {getString('common.v')})
                                    </div>
                                    <div className="mt-3">
                                        <div className="text-sm text-gray-600 dark:text-gray-400 font-semibold">{getString('common.thermostat')}</div>
                                        <div className="mt-1" style={{ direction: 'ltr' }}>
                                            {iceBlockCoolingUnit.ntc1! < 3050 ? (
                                                <>
                                                    {mvToTemp(iceBlockCoolingUnit.ntc1!)[0]} {getString('common.celsius')}
                                                </>
                                            ) : (
                                                <div className="text-sm text-red-500">{getString('common.disconnected')}</div>
                                            )}
                                        </div>
                                        <div className="mt-3 text-xs">
                                            {iceBlockCoolingUnit.ntc1! / 1000}
                                            {getString('common.v')}
                                        </div>
                                    </div>
                                    <div className="text-sm mt-3">
                                        <div className="text-gray-600 dark:text-gray-400 font-semibold">{getString('common.active_time')}</div>
                                        <div>{safeReadableTime(iceBlockCoolingUnit.compressorActiveTime!)}</div>
                                    </div>
                                    <div className="text-sm mt-3">
                                        <div className="text-gray-600 dark:text-gray-400 font-semibold">{getString('common.inactive_time')}</div>
                                        <div>{safeReadableTime(iceBlockCoolingUnit.compressorInactiveTime!)}</div>
                                    </div>
                                </>
                            )}
                            {iceBankStateLabel !== undefined && (
                                <div className="text-sm mt-3">
                                    <div className="text-gray-600 dark:text-gray-400 font-semibold">{getString('system_info.icebank_state')}</div>
                                    <div>{iceBankStateLabel}</div>
                                </div>
                            )}
                        </div>,
                    )}

                {aluBlockCoolingUnit &&
                    this.renderRow(
                        getString('system_info.cooling_unit'),
                        <div className="mt-3">
                            <div className="flex flex-col items-center">{RegularState[Number(aluBlockCoolingUnit.compressorState)]}</div>
                            <div className="mt-3">
                                <div className="text-sm text-gray-600 dark:text-gray-400 font-semibold">{getString('common.thermostat')}</div>
                                <div className="mt-1" style={{ direction: 'ltr' }}>
                                    {aluBlockCoolingUnit.ntc1 < 3050 ? (
                                        <>
                                            {mvToTemp(aluBlockCoolingUnit.ntc1)[0]} {getString('common.celsius')}
                                        </>
                                    ) : (
                                        <div className="text-sm text-red-500">{getString('common.disconnected')}</div>
                                    )}
                                </div>
                                <div className="mt-3 text-xs">
                                    {aluBlockCoolingUnit.ntc1 / 1000}
                                    {getString('common.v')}
                                </div>
                            </div>
                            <div className="text-sm mt-3">
                                <div className="text-gray-600 dark:text-gray-400 font-semibold">{getString('common.active_time')}</div>
                                <div>{safeReadableTime(aluBlockCoolingUnit.compressorActiveTime)}</div>
                            </div>
                            <div className="text-sm mt-3">
                                <div className="text-gray-600 dark:text-gray-400 font-semibold">{getString('common.inactive_time')}</div>
                                <div>{safeReadableTime(aluBlockCoolingUnit.compressorInactiveTime)}</div>
                            </div>
                        </div>,
                    )}

                {sodaUnit &&
                    this.renderRow(
                        getString('system_info.soda_unit'),
                        <div className="mt-3">
                            <div className="flex flex-col items-center text-sm">
                                {PumpStateFunc[Number(sodaUnit.pumpState)](PumpStatus[sodaUnit.pumpState])}
                            </div>
                            {/* <div className="text-sm mt-3">
                                    <div className="text-gray-600 dark:text-gray-400 font-semibold">{getString('common.status')}</div>
                                    <div>{PumpStatus[sodaUnit.pumpState]}</div>
                                </div> */}

                            <div className="text-base mt-3">
                                <div className="mb-2 text-gray-600 dark:text-gray-400 font-semibold">{getString('system_info.liquid_level')}</div>
                                {/* {!sodaUnit.oneLiquidLevel && ( */}
                                <div className="mb-3 flex items-center justify-center">
                                    <div className="me-5 text-sm text-gray-600 dark:text-gray-400 font-semibold">
                                        {getString('system_info.liquid_high')}
                                    </div>
                                    <div>{RegularState[Number(sodaUnit.higherSensor)]}</div>
                                </div>
                                {/* )} */}
                                <div className="flex flex-row justify-center items-center">
                                    {/* {!sodaUnit.oneLiquidLevel && ( */}
                                    <>
                                        <div className="me-5 text-sm text-gray-600 dark:text-gray-400 font-semibold">
                                            {getString('system_info.liquid_low')}
                                        </div>
                                        <div>{RegularState[Number(sodaUnit.lowerSensor)]}</div>
                                    </>
                                    {/* )} */}
                                </div>
                            </div>

                            {!sodaUnit.ignoreH2oPiezo && (
                                <>
                                    <div className="mt-3 text-base text-gray-600 dark:text-gray-400 font-semibold">
                                        {getString('system_info.piezo1')}
                                    </div>
                                    <div>
                                        {sodaUnit.piezo1 < 3050 ? (
                                            <div className={classNames('mt-3')} style={{ direction: getLocale() === 'he' ? 'rtl' : 'ltr' }}>
                                                {mvToBar(sodaUnit.piezo1)} {getString('common.bar')}
                                            </div>
                                        ) : (
                                            <div className="text-sm text-red-500">{getString('common.disconnected')}</div>
                                        )}
                                        <div className={classNames('mt-3 text-xs')}>
                                            {sodaUnit.piezo1 / 1000}
                                            {getString('common.v')}
                                        </div>
                                    </div>
                                </>
                            )}

                            {!sodaUnit.ignoreCo2Piezo && (
                                <>
                                    <div className="mt-3 text-base text-gray-600 dark:text-gray-400 font-semibold">
                                        {getString('system_info.piezo2')}
                                    </div>
                                    {sodaUnit.piezo2 < 3050 ? (
                                        <div style={{ direction: getLocale() === 'he' ? 'rtl' : 'ltr' }}>
                                            {mvToBar(sodaUnit.piezo2)} {getString('common.bar')}
                                        </div>
                                    ) : (
                                        <div className={classNames('mt-3 text-sm text-red-500')}>{getString('common.disconnected')}</div>
                                    )}
                                    <div className={classNames('mt-3 text-xs')}>
                                        {sodaUnit.piezo2 / 1000}
                                        {getString('common.v')}
                                    </div>
                                </>
                            )}

                            <div className="text-sm mt-3">
                                <div className="text-gray-600 dark:text-gray-400 font-semibold">{getString('common.active_time')}</div>
                                <div>{safeReadableTime(sodaUnit.pumpActiveTime)}</div>
                            </div>
                            <div className="text-sm mt-3">
                                <div className="text-gray-600 dark:text-gray-400 font-semibold">{getString('common.inactive_time')}</div>
                                <div>{safeReadableTime(sodaUnit.pumpInactiveTime)}</div>
                            </div>
                        </div>,
                    )}

                {ambientUnit &&
                    this.renderRow(
                        getString('system_info.ambient_unit'),
                        <div className="mt-3">
                            <div className="flex flex-col items-center">{RegularState[Number(ambientUnit.state)]}</div>
                            <div className="mt-3">
                                <div className="text-sm text-gray-600 dark:text-gray-400 font-semibold">{getString('common.thermostat')}</div>
                                <div className="mt-1" style={{ direction: 'ltr' }}>
                                    {ambientUnit.ambient < 3050 ? (
                                        <>
                                            {mvToTemp(ambientUnit.ambient)[0]} {getString('common.celsius')}
                                        </>
                                    ) : (
                                        <div className="text-sm text-red-500">{getString('common.disconnected')}</div>
                                    )}
                                </div>
                                <div className="mt-3 text-xs">
                                    {ambientUnit.ambient / 1000}
                                    {getString('common.v')}
                                </div>
                            </div>
                            <div className="text-sm mt-3">
                                <div className="text-gray-600 dark:text-gray-400 font-semibold">{getString('common.active_time')}</div>
                                <div>{safeReadableTime(ambientUnit.fanActiveTime)}</div>
                            </div>
                            <div className="text-sm mt-3">
                                <div className="text-gray-600 dark:text-gray-400 font-semibold">{getString('common.inactive_time')}</div>
                                <div>{safeReadableTime(ambientUnit.fanInactiveTime)}</div>
                            </div>
                        </div>,
                    )}

                {circulationPumpUnit &&
                    this.renderRow(
                        getString('system_info.circulation_pump'),
                        <div className="mt-3">
                            <div className="flex flex-col items-center">{RegularState[Number(circulationPumpUnit.state)]}</div>
                            <div className="text-sm mt-3">
                                <div className="text-gray-600 dark:text-gray-400 font-semibold">{getString('common.active_time')}</div>
                                <div>{safeReadableTime(circulationPumpUnit.circPumpActiveTime)}</div>
                            </div>
                            <div className="text-sm mt-3">
                                <div className="text-gray-600 dark:text-gray-400 font-semibold">{getString('common.inactive_time')}</div>
                                <div>{safeReadableTime(circulationPumpUnit.circPumpInactiveTime)}</div>
                            </div>
                        </div>,
                    )}

                {heatingUnit &&
                    this.renderRow(
                        getString('system_info.boiler_state'),
                        <div className="mt-3">
                            <div className="flex flex-col items-center">{RegularState[Number(heatingUnit.boilerState)]}</div>
                            <div className="text-sm mt-3">
                                <div className="text-gray-600 dark:text-gray-400 font-semibold">{getString('system_info.boiler_active_time')}</div>
                                <div>{safeReadableTime(heatingUnit.boilerActiveTime)}</div>
                            </div>
                            <div className="text-sm mt-3">
                                <div className="text-gray-600 dark:text-gray-400 font-semibold">{getString('system_info.boiler_inactive_time')}</div>
                                <div>{safeReadableTime(heatingUnit.boilerInactiveTime)}</div>
                            </div>
                        </div>,
                    )}

                {relaySolenoids &&
                    this.renderRow(
                        getString('system_info.rel_sols'),
                        <div className="mt-3 grid grid-cols-2 gap-4">
                            {Object.entries(relaySolenoids).map(([key, value], i) => {
                                const relSolNum = key.match(/relSol(\d+)State/)![1]

                                return (
                                    <div key={key} className="text-sm">
                                        {RegularStateFunc[String(value)](String(relSolNum))}
                                    </div>
                                )
                            })}
                        </div>,
                    )}

                {filterFiller &&
                    this.renderRow(
                        getString('system_info.filter_filler'),
                        <div className="mt-3">
                            <div className="flex flex-col items-center text-sm">
                                {FilterFillerStateFunc[Number(filterFiller.state)](FilterFillerState[filterFiller.state])}
                            </div>
                            <div className="text-sm mt-3">
                                <div className="text-gray-600 dark:text-gray-400 font-semibold">{getString('common.counter')}</div>
                                <div>{filterFiller.counter}</div>
                            </div>
                            <div className="text-sm mt-3">
                                <div className="text-gray-600 dark:text-gray-400 font-semibold">{getString('common.active_time')}</div>
                                <div>{safeReadableTime(filterFiller.filterFillerActiveTime)}</div>
                            </div>
                            <div className="text-sm mt-3">
                                <div className="text-gray-600 dark:text-gray-400 font-semibold">{getString('common.inactive_time')}</div>
                                <div>{safeReadableTime(filterFiller.filterFillerInactiveTime)}</div>
                            </div>
                        </div>,
                    )}

                {shabbatInfo &&
                    this.renderRow(
                        getString('system_info.shabbat_info'),
                        <div className="mt-3 grid grid-cols-2 gap-4">
                            <div className="text-sm">
                                <div className="mb-2 text-gray-600 dark:text-gray-400 font-semibold">
                                    {getString('system_info.shabbat_setting_enabled')}
                                </div>
                                <div className="flex justify-center">{RegularState[Number(shabbatInfo.shabbatSettingEnabled)]}</div>
                            </div>
                            <div className="text-sm">
                                <div className="mb-2 text-gray-600 dark:text-gray-400 font-semibold">
                                    {getString('system_info.shabbat_active')}
                                </div>
                                <div className="flex justify-center">{RegularState[Number(shabbatInfo.shabbatActive)]}</div>
                            </div>
                        </div>,
                    )}
            </div>
        )
    }

    renderRow(label: string, value: any, ltr = false, message?: any, size?: 'xs' | 'sm' | 'base' | 'lg' | 'xl' | '2xl') {
        return (
            <div className={classNames('w-56 text-center bg-gray-100 dark:bg-gray-900 m-1 flex flex-col items-center', ltr && 'ltr')}>
                <div className="p-2 pb-0 text-gray-600 dark:text-gray-400 font-semibold">{label}</div>
                <div className={classNames(`p-4 pt-0 text-black dark:text-white font-bold text-${size || '2xl'} leading-7`)}>
                    {this.renderValue(value)} {message}
                </div>
            </div>
        )
    }

    renderValue(value: any) {
        const { common } = this.state
        if (common === undefined) return <Loader />
        return value
    }
}

export default FragmentedSystemInfoTab
